import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';

/**
 * SearchInput — มี debounce ในตัว และรับคีย์ลัด '/' เพื่อโฟกัสตัวเอง
 * (focusOnSlash = true ให้ใช้ได้ตัวเดียวต่อหน้า)
 */
export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** ms — ค่าเริ่มต้น 300ms เร็วพอให้รู้สึกตอบสนอง และไม่ยิง request ทุกตัวอักษร */
  debounceMs?: number;
  focusOnSlash?: boolean;
  /** ข้อความ label สำหรับ screen reader */
  label?: string;
  width?: number | string;
  className?: string;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { value, onChange, placeholder = 'ค้นหา', debounceMs = 300, focusOnSlash = false, label = 'ค้นหา', width = 280, className },
  forwarded,
) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(forwarded, () => inputRef.current as HTMLInputElement);

  useEffect(() => { setDraft(value); }, [value]);

  useEffect(() => {
    if (draft === value) return;
    const t = window.setTimeout(() => onChange(draft), debounceMs);
    return () => window.clearTimeout(t);
  }, [draft, value, onChange, debounceMs]);

  useKeyboardShortcuts(
    [{ key: '/', description: 'โฟกัสช่องค้นหา', group: 'ทั่วไป', handler: () => inputRef.current?.focus() }],
    focusOnSlash,
  );

  return (
    <div className={cn('relative', className)} style={{ width }}>
      <Icon name="search" size={18} className="pointer-events-none absolute left-[var(--space-25)] top-1/2 -translate-y-1/2 text-[var(--ink-400)]" />
      <input
        ref={inputRef}
        type="search"
        value={draft}
        aria-label={label}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') { setDraft(''); onChange(''); } }}
        className="min-h-[var(--hit-control)] w-full rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] pl-[var(--space-8)] pr-[var(--space-8)] font-[family-name:var(--font-thai)] text-[length:var(--text-form)] leading-[var(--leading-form)] text-[var(--ink-900)] placeholder:text-[var(--ink-400)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
      />
      {draft && (
        <button
          type="button"
          aria-label="ล้างคำค้น"
          onClick={() => { setDraft(''); onChange(''); inputRef.current?.focus(); }}
          className="absolute right-[var(--space-1)] top-1/2 inline-flex h-[var(--hit-icon-sm)] w-[var(--hit-icon-sm)] -translate-y-1/2 items-center justify-center rounded-[var(--radius-sm)] text-[var(--ink-600)] hover:bg-[var(--surface-100)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
        >
          <Icon name="close" size={16} />
        </button>
      )}
      {focusOnSlash && (
        <kbd
          aria-hidden
          className="pointer-events-none absolute right-[var(--space-25)] top-1/2 -translate-y-1/2 rounded-[var(--radius-xs)] border border-[var(--line-300)] bg-[var(--surface-100)] px-1 font-[family-name:var(--font-num)] text-[length:var(--text-micro)] text-[var(--ink-600)]"
          style={{ display: draft ? 'none' : undefined }}
        >
          /
        </kbd>
      )}
    </div>
  );
});
